import StimulusDialog from "@stimulus-components/dialog";

export default class Dialog extends StimulusDialog {
  static values = {
    keepContent: { type: Boolean, default: false },
  };
  static targets = ["dialog"];

  // Track si le mousedown a eu lieu sur le backdrop
  mouseDownOnBackdrop = false;

  connect() {
    super.connect();

    //on par ça pour avoir le même comportement si on ferme avec la souris ou le clavier
    this.dialogTarget.addEventListener("close", () => this.close());

    // Tracker le mousedown pour éviter de fermer la modale quand on drag depuis l'intérieur
    this.boundHandleMouseDown = this.handleMouseDown.bind(this);
    this.element.addEventListener("mousedown", this.boundHandleMouseDown);
  }

  disconnect() {
    if (this.boundHandleMouseDown) {
      this.element.removeEventListener("mousedown", this.boundHandleMouseDown);
    }
  }

  handleMouseDown(event) {
    // Vérifier si le mousedown est sur le backdrop (l'élément dialog lui-même, pas son contenu)
    this.mouseDownOnBackdrop = event.target === this.dialogTarget;
  }

  // Override de backdropClose pour ne fermer que si mousedown ET click sont sur le backdrop
  // se déclenche sur le mouseup (on relache le clic), donc on check si appui + relache sont sur le backdrop
  backdropClose(event) {
    // Ne fermer que si le mousedown a aussi eu lieu sur le backdrop
    if (this.mouseDownOnBackdrop && event.target === this.dialogTarget) {
      this.close();
    }
    // Reset le flag
    this.mouseDownOnBackdrop = false;
  }
  // Function to override on open.
  open(event) {
    const eventId = event.detail?.id;
    const elementId = this.element?.id;

    if ((eventId && eventId === elementId) || !eventId) {
      // a-t-on un turboFrame dans la modale ?
      const turboFrame = this.element.querySelector("turbo-frame");

      if (turboFrame && !this.keepContentValue) {
        // Si oui on l'attend
        this.waitForFrame(turboFrame);
      } else {
        this.dispatchOpening();
        super.open();
      }
    }
  }

  async waitForFrame(frame) {
    await new Promise((resolve) => requestAnimationFrame(resolve));

    if (frame.hasAttribute("busy")) {
      frame.addEventListener("turbo:frame-load", () => super.open(), {
        once: true,
      });
      frame.addEventListener("turbo:frame-missing", () => super.open(), {
        once: true,
      });
    } else {
      super.open();
    }
  }

  dispatchOpening() {
    this.element.dispatchEvent(
      new CustomEvent("dialog:opened", { bubbles: true }),
    );
  }

  // Met en pause tout média (video/audio) contenu dans la modale à sa fermeture.
  // Sinon un <video autoplay> continue de jouer avec le son en arrière-plan.
  // On vide aussi le turbo-frame (contenu non conservé) : sans ça, à la
  // réouverture, le rechargement lazy du frame entre en course avec la
  // navigation du lien et empile un second lecteur (double son).
  close() {
    const hadMedia = this.pauseNestedMedia();
    super.close();
    if (hadMedia && !this.keepContentValue) this.resetFrame();
  }

  pauseNestedMedia() {
    const media = this.element.querySelectorAll("video, audio");
    media.forEach((el) => {
      if (!el.paused) el.pause();
    });
    return media.length > 0;
  }

  resetFrame() {
    const frame = this.element.querySelector("turbo-frame");
    if (!frame) return;

    frame.removeAttribute("src");
    frame.removeAttribute("complete");
    frame.innerHTML = "";
  }

  closeFromOutside(event) {
    if (!event) {
      this.close();
    }
    const eventId = event.detail?.id;
    const elementId = this.element?.id;
    if (eventId) {
      // Pourquoi filtrons nous si l'event a un id et que l'element en a un aussi ?
      if (eventId === elementId) {
        this.element.dispatchEvent(
          new CustomEvent("dialog:closed", {
            detail: { closed: true }, // Données optionnelles
            bubbles: true, // Permet à l’événement de remonter dans le DOM
          }),
        );
        this.close();
      }
    } else if (!elementId) {
      this.close();
    }
  }

  closeOnSuccess(event) {
    if (event.detail?.success) this.close();
  }

  // Ajout d'une fonction de fermeture pour ne pas retoucher à closeFromOutside
  forceClosing() {
    this.element.dispatchEvent(
      new CustomEvent("dialog:closed", {
        detail: { closed: true }, // Données optionnelles
        bubbles: true, // Permet à l'événement de remonter dans le DOM
      }),
    );
    this.close();
  }
}
